import React from 'react';
import { Order } from '../../pages/Orders';
import { CheckCircleIcon, XCircleIcon, ClockIcon, CalendarIcon, UserIcon, MailIcon, PhoneIcon, ShoppingCartIcon, AlertTriangleIcon, ChevronRightIcon } from 'lucide-react';
interface OrderListProps {
  orders: Order[];
  onOrderClick: (order: Order) => void;
}
export function OrderList({
  orders,
  onOrderClick
}: OrderListProps) {
  if (orders.length === 0) {
    return <div className="p-8 text-center" data-id="element-1170">
        <p className="text-gray-500" data-id="element-1171">No orders found</p>
      </div>;
  }
  // Get status badge with appropriate colors
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800" data-id="element-1172">
            New
          </span>;
      case 'in-progress':
        return <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800" data-id="element-1173">
            In Progress
          </span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800" data-id="element-1174">
            Completed
          </span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800" data-id="element-1175">
            Cancelled
          </span>;
      case 'open':
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800" data-id="element-1176">
            Open
          </span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800" data-id="element-1177">
            {status}
          </span>;
    }
  };
  // Get payment status icon
  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-1178" />;
      case 'unpaid':
        return <ClockIcon size={16} className="text-amber-500" data-id="element-1179" />;
      case 'cancelled':
        return <XCircleIcon size={16} className="text-red-500" data-id="element-1180" />;
      default:
        return null;
    }
  };
  return <div className="overflow-x-auto" data-id="element-1181">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1182">
        <thead className="bg-gray-50" data-id="element-1183">
          <tr data-id="element-1184">
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1185">
              Order
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1186">
              Date
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1187">
              Customer
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1188">
              Status
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1189">
              Payment
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1190">
              Total
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1191">
              Items
            </th>
            <th scope="col" className="relative px-4 py-3" data-id="element-1192">
              <span className="sr-only" data-id="element-1193">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1194">
          {orders.map(order => <tr key={order.id} onClick={() => onOrderClick(order)} className="hover:bg-gray-50 cursor-pointer" data-id="element-1195">
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1196">
                <div className="text-sm font-medium text-gray-900" data-id="element-1197">
                  #{order.id}
                </div>
                {order.transaction && <div className="text-xs text-gray-500 truncate max-w-[120px]" data-id="element-1198">
                    {order.transaction}
                  </div>}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1199">
                <div className="flex items-center text-sm text-gray-500" data-id="element-1200">
                  <CalendarIcon size={14} className="mr-1" data-id="element-1201" />
                  {order.date}
                </div>
                <div className="flex items-center text-sm text-gray-500" data-id="element-1202">
                  <ClockIcon size={14} className="mr-1" data-id="element-1203" />
                  {order.time}
                </div>
              </td>
              <td className="px-4 py-4" data-id="element-1204">
                <div className="flex items-center" data-id="element-1205">
                  <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-1206">
                    <UserIcon size={14} className="text-gray-500" data-id="element-1207" />
                  </div>
                  <div className="ml-3" data-id="element-1208">
                    <div className="text-sm font-medium text-gray-900" data-id="element-1209">
                      {order.customer.name}
                    </div>
                    <div className="flex items-center text-xs text-gray-500" data-id="element-1210">
                      <MailIcon size={12} className="mr-1" data-id="element-1211" />
                      <span className="truncate max-w-[120px]" data-id="element-1212">
                        {order.customer.email}
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500" data-id="element-1213">
                      <PhoneIcon size={12} className="mr-1" data-id="element-1214" />
                      {order.customer.mobileNumber}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1215">
                {getStatusBadge(order.status)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1216">
                <div className="flex items-center" data-id="element-1217">
                  {getPaymentStatusIcon(order.paymentStatus)}
                  <span className="ml-1.5 text-sm text-gray-700 capitalize" data-id="element-1218">
                    {order.paymentStatus}
                  </span>
                </div>
                {order.transactionStatus && <div className="text-xs text-gray-500 capitalize mt-1" data-id="element-1219">
                    {order.transactionStatus}
                  </div>}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1220">
                ${order.total.toFixed(2)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1221">
                <div className="flex items-center text-sm text-gray-500" data-id="element-1222">
                  <ShoppingCartIcon size={14} className="mr-1" data-id="element-1223" />
                  <span data-id="element-1224">
                    {order.items.reduce((sum, item) => sum + item.quantity, 0)}{' '}
                    items
                  </span>
                </div>
                <div className="text-xs text-gray-500 truncate max-w-[120px] mt-1" data-id="element-1225">
                  {order.items.map(item => `${item.quantity}x ${item.name}`).join(', ')}
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1226">
                <ChevronRightIcon size={16} className="text-gray-400" data-id="element-1227" />
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}